import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { SelectOption } from '@vchasno/ui-kit';

import { TaskTemplateStatus, TaskTemplateType, appApi } from 'api/api';

export enum CreateTaskStep {
  TaskTemplate = 'TaskTemplate',
  VacationPeriod = 'VacationPeriod',
}

interface TaskTemplateState {
  versionId: string;
  type: TaskTemplateType;
}

interface CompanyState {
  id: string;
  name: string;
}

interface EmployeeState {
  id: string;
  name: string;
  companyId: string;
}

interface TaskPeriod {
  start: Nullable<Date>;
  end: Nullable<Date>;
}

interface StartActionPayload {
  step: CreateTaskStep;
  company?: CompanyState;
  employee?: EmployeeState;
}

interface CreateTaskSliceState {
  taskTemplate: Nullable<TaskTemplateState>;
  step: Nullable<CreateTaskStep>;
  stepHistory: CreateTaskStep[];
  company: Nullable<CompanyState>;
  employee: Nullable<EmployeeState>;
  period: Nullable<TaskPeriod>;
}

const initialState: CreateTaskSliceState = {
  step: null,
  stepHistory: [],
  taskTemplate: null,
  company: null,
  employee: null,
  period: null,
};

export const createTaskPopupSlice = createSlice({
  name: 'createTaskPopupSlice',
  initialState,
  reducers: {
    start: (state, action: PayloadAction<StartActionPayload>) => {
      state.step = action.payload.step;

      if (action.payload.company) {
        state.company = action.payload.company;
      }

      if (action.payload.employee) {
        state.employee = action.payload.employee;
      }

      state.stepHistory = [];
    },
    setStep: (state, action: PayloadAction<CreateTaskStep>) => {
      state.step = action.payload;
    },
    setPeriodDate: (
      state,
      action: PayloadAction<[Nullable<Date>, Nullable<Date>]>,
    ) => {
      const [start, end] = action.payload;

      state.period = { start, end };
    },
    setCompany: (state, action: PayloadAction<Nullable<CompanyState>>) => {
      state.company = action.payload;
      state.taskTemplate = null;
      state.period = null;
    },
    handleCompanyChange: (state, action: PayloadAction<SelectOption>) => {
      state.company = {
        id: action.payload.value,
        name: action.payload.label,
      };

      state.employee = null;
      state.taskTemplate = null;
      state.period = null;
    },
    setEmployee: (state, action: PayloadAction<EmployeeState>) => {
      state.employee = action.payload;
      state.period = null;
    },
    setTaskTemplate: (state, action: PayloadAction<TaskTemplateState>) => {
      state.taskTemplate = action.payload;
    },
    resetState: () => ({
      ...initialState,
    }),
    nextStep: (state) => {
      /* TODO implement different logic for different task types */
      if (state.step) {
        state.stepHistory.push(state.step);
      }

      state.step = CreateTaskStep.VacationPeriod;
    },
    popStep: (state) => {
      const lastStep = state.stepHistory.pop();

      state.step = lastStep || null;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      appApi.endpoints.listTaskTemplates.matchFulfilled,
      (state, action) => {
        if (state.taskTemplate || !action.payload || !state.company) {
          return state;
        }

        const firstTaskTemplate = action.payload.detail.items.filter(
          (el) => el.status === TaskTemplateStatus.Active,
        )[0];

        if (!firstTaskTemplate) return state;

        state.taskTemplate = {
          versionId: firstTaskTemplate.version_identifier,
          type: firstTaskTemplate.type,
        };
      },
    );
  },
  selectors: {
    selectStep: (state) => state.step,
    selectCompany: (state) => state.company,
    selectEmployee: (state) => state.employee,
    selectEmployeeId: (state) => state.employee?.id ?? null,
    selectTaskTemplate: (state) => state.taskTemplate,
    selectTaskTemplateVersionId: (state) =>
      state.taskTemplate?.versionId ?? null,
    selectPeriod: (state) => state.period,
  },
});
